// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
//
// State machine + live pipeline orchestrator for Бриф.
//
// The chat is wired to the REAL Hermes pipeline (SPEC §1):
//   send → chatPlan → suggestions → suggestion-picker cards
//   → pick + channel selection → createPost(skip_ai) → preflight → publish
// Each step pushes an assistant ("Hermes") message in the existing Бриф card
// style. The mock pools in data.ts are kept ONLY as a graceful fallback when the
// API is unreachable, so a local demo still renders something.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  buildContent,
  CHATS,
  draftCardFromSuggestion,
  detectLanguage,
  INTENT_PRESETS,
  type BriefMessage,
  type BriefMode,
  type ChannelsCard,
  type ChatDef,
  type PlanCard,
  type SuggestionDef,
} from './data';
import {
  chatPlan,
  createFromChat,
  hashtags as fetchHashtags,
  suggestions as fetchSuggestions,
  translate as fetchTranslate,
  type SelectedChannel,
} from './promo/chatApi';
import { preflight, publish } from './promo/postsApi';
import { getPreferences, savePreferences } from './promo/analyticsApi';
import { bufferChannels, bufferOrganizations, getConnections, getHealth } from './promo/channelsApi';
import type {
  ChatChannelOption,
  PlatformDraft,
  Platform,
  PromotionSuggestion,
  SourceContent,
} from './promo/types';

export type BriefView = 'home' | 'chat';
export type BriefScreen = 'chat' | 'radar' | 'overview' | 'queue' | 'calendar' | 'channels' | 'templates';

export interface BriefController {
  view: BriefView;
  screen: BriefScreen;
  mode: BriefMode;
  draft: string;
  messages: BriefMessage[];
  activeChat: number | null;
  toast: string;
  timezone: string;
  scrollRef: (el: HTMLDivElement | null) => void;

  setScreen: (s: BriefScreen) => void;
  setMode: (id: BriefMode) => void;
  setDraft: (value: string) => void;
  goHome: () => void;
  send: (textArg?: string) => void;
  sendSuggestion: (s: SuggestionDef) => void;
  loadChat: (c: ChatDef, idx: number) => void;
  regen: (id: number, mode: BriefMode) => void;
  copyText: (text: string) => void;
  notify: (text: string) => void;

  // ── templates (B4) ───────────────────────────────────────────────
  useTemplate: (source: SourceContent) => void;

  // ── timezone (B7) ────────────────────────────────────────────────
  setTimezone: (tz: string) => Promise<void>;

  // ── live pipeline actions ────────────────────────────────────────
  pickSuggestion: (messageId: number, suggestionId: string) => void;
  toggleChannel: (messageId: number, channelId: string) => void;
  confirmChannels: (messageId: number) => void;

  // ── per-platform draft tools (B5 + B7) ───────────────────────────
  setDraftEditing: (messageId: number, platform: Platform | null) => void;
  setDraftEdit: (messageId: number, platform: Platform, body: string) => void;
  appendHashtags: (messageId: number, platform: Platform, body: string) => void;
  translateDraft: (messageId: number, platform: Platform, body: string, language: 'ru' | 'en') => void;
  clearTranslation: (messageId: number, platform: Platform) => void;
}

// Direct-publish channels — the worker exposes telegram + twitter as direct.
const DIRECT_PLATFORMS: Platform[] = ['telegram', 'twitter'];

export function useBrief(): BriefController {
  const [view, setView] = useState<BriefView>('home');
  const [screen, setScreen] = useState<BriefScreen>('chat');
  const [mode, setMode] = useState<BriefMode>('text');
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState<BriefMessage[]>([]);
  const [activeChat, setActiveChat] = useState<number | null>(null);
  const [toast, setToast] = useState('');
  // Timezone (B7) — seeded from the browser, refined by the server preference.
  const [timezone, setTimezoneState] = useState<string>(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  });

  const nid = useRef(1);
  const scrollEl = useRef<HTMLDivElement | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirror of `messages` so pipeline callbacks can read the latest list
  // synchronously (a setState updater runs later, so reading a value it assigns
  // right after dispatch is unreliable — it froze the pick→channels step).
  const messagesRef = useRef<BriefMessage[]>([]);
  messagesRef.current = messages;

  const scrollRef = useCallback((el: HTMLDivElement | null) => {
    scrollEl.current = el;
  }, []);

  // Auto-scroll to bottom whenever the message list changes.
  useEffect(() => {
    if (scrollEl.current) scrollEl.current.scrollTop = scrollEl.current.scrollHeight;
  }, [messages]);

  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  // Load the persisted timezone once (soft read — never logs out). The browser
  // default already seeded the state, so we only override when the server has a
  // stored value.
  useEffect(() => {
    let alive = true;
    getPreferences().then((r) => {
      if (alive && r.data.timezone) setTimezoneState(r.data.timezone);
    });
    return () => {
      alive = false;
    };
  }, []);

  const notify = useCallback((text: string) => {
    setToast(text);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 1700);
  }, []);

  // Small helper: replace a message by id.
  const patch = useCallback((id: number, next: BriefMessage | ((m: BriefMessage) => BriefMessage)) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? (typeof next === 'function' ? next(m) : next) : m)),
    );
  }, []);

  const append = useCallback((msg: BriefMessage) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  const goHome = useCallback(() => {
    setScreen('chat');
    setView('home');
    setMessages([]);
    setActiveChat(null);
    setDraft('');
  }, []);

  // ── Pipeline step 1+2: plan → suggestions ────────────────────────
  const runPipeline = useCallback(
    async (prompt: string, currentMode: BriefMode, thinkingId: number) => {
      const preset = INTENT_PRESETS[currentMode];
      const language = detectLanguage(prompt);

      // 1a. chat-plan — derive plan defaults (degrades to rules; never 4xx for
      // AI failures). Merge over the mode preset.
      let planCard: PlanCard = {
        language,
        sourceType: preset.sourceType,
        sourceMode: preset.sourceMode,
        strategy: preset.strategy,
        marketingSkill: preset.marketingSkill,
        topic: prompt,
        platforms: DIRECT_PLATFORMS,
        requireCover: preset.requireCover,
        requireDeepLink: preset.requireDeepLink,
      };

      let apiReachable = true;
      try {
        const planRes = await chatPlan({
          prompt,
          ready_platforms: DIRECT_PLATFORMS,
          selected_channels: [],
        });
        const p = planRes.plan;
        if (p) {
          const platforms = normalizePlatforms(p.platforms) ?? planCard.platforms;
          planCard = {
            language: p.language ?? planCard.language,
            sourceType: p.source_type ?? p.sourceType ?? planCard.sourceType,
            sourceMode: p.source_mode ?? p.sourceMode ?? planCard.sourceMode,
            strategy: p.strategy ?? planCard.strategy,
            marketingSkill: p.marketing_skill ?? p.marketingSkill ?? planCard.marketingSkill,
            topic: p.topic ?? planCard.topic,
            platforms,
            requireCover: p.require_cover ?? p.requireCover ?? planCard.requireCover,
            requireDeepLink: p.require_deep_link ?? p.requireDeepLink ?? planCard.requireDeepLink,
            fallback: planRes.fallback,
            warning: planRes.warning,
          };
        } else {
          planCard.fallback = true;
        }
        // world_books forces book source type (SPEC §1a).
        if (planCard.sourceMode === 'world_books') planCard.sourceType = 'book';
      } catch {
        apiReachable = false;
        planCard.fallback = true;
      }

      // Replace the thinking bubble with the plan card.
      const planMsg: BriefMessage = {
        id: thinkingId,
        role: 'assistant',
        kind: 'plan',
        mode: currentMode,
        text: 'Собрал план кампании — теперь подберу кандидатов из библиотеки.',
        plan: planCard,
      };
      patch(thinkingId, planMsg);

      // 1b. suggestions — the core candidate-selection step.
      const pickerId = nid.current++;
      append({ id: pickerId, role: 'assistant', kind: 'thinking', mode: currentMode });

      let candidates: PromotionSuggestion[] = [];
      let provider: string | undefined;
      let note: string | undefined;
      let fallback = !apiReachable;

      if (apiReachable) {
        try {
          const res = await fetchSuggestions({
            language: planCard.language,
            source_type: planCard.sourceType,
            source_mode: planCard.sourceMode,
            strategy: planCard.strategy,
            marketing_skill: planCard.marketingSkill,
            topic: planCard.topic,
            prompt,
            intent: preset.intent,
            require_cover: planCard.requireCover,
            require_deep_link: planCard.requireDeepLink,
            count: 3,
          });
          candidates = Array.isArray(res.suggestions) ? res.suggestions : [];
          provider = res.selection_provider ?? res.codex_selection?.provider;
          note = res.codex_selection?.note ?? res.error;
          fallback = res.selection_provider === 'rules' || candidates.length === 0;
        } catch {
          apiReachable = false;
          fallback = true;
        }
      }

      // No demo fallback: when there are no real candidates, show an honest
      // empty (online) or error (offline) state instead of synthetic cards.
      if (candidates.length === 0) {
        patch(
          pickerId,
          apiReachable
            ? {
                id: pickerId,
                role: 'assistant',
                kind: 'plain',
                mode: currentMode,
                text: 'По этому запросу в библиотеке ничего не нашлось. Уточните тему или выберите другой режим.',
              }
            : {
                id: pickerId,
                role: 'assistant',
                kind: 'error',
                mode: currentMode,
                text: 'Не удалось связаться с сервером. Проверьте подключение и попробуйте снова.',
              },
        );
        return;
      }

      patch(pickerId, {
        id: pickerId,
        role: 'assistant',
        kind: 'suggestions',
        mode: currentMode,
        text: 'Вот кандидаты под план. Выберите один — соберу каналы и черновики.',
        picker: { suggestions: candidates, pickedId: null, provider, fallback, note },
      });
    },
    [append, patch],
  );

  const startSend = useCallback(
    (text: string, currentMode: BriefMode) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const uid = nid.current++;
      const aid = nid.current++;
      const user: BriefMessage = { id: uid, role: 'user', kind: 'plain', text: trimmed };
      const think: BriefMessage = { id: aid, role: 'assistant', kind: 'thinking', mode: currentMode };
      setScreen('chat');
      setView('chat');
      setDraft('');
      setMessages((prev) => [...prev, user, think]);
      void runPipeline(trimmed, currentMode, aid).catch((err) => {
        patch(aid, {
          id: aid,
          role: 'assistant',
          kind: 'error',
          mode: currentMode,
          text: err instanceof Error ? err.message : 'Не удалось собрать кампанию.',
        });
      });
    },
    [patch, runPipeline],
  );

  const send = useCallback(
    (textArg?: string) => {
      startSend(textArg != null ? textArg : draft, mode);
    },
    [draft, mode, startSend],
  );

  const sendSuggestion = useCallback(
    (s: SuggestionDef) => {
      setMode(s.mode);
      startSend(s.q, s.mode);
    },
    [startSend],
  );

  const loadChat = useCallback(
    (c: ChatDef, idx: number) => {
      setMode(c.mode);
      setActiveChat(idx);
      startSend(c.q, c.mode);
    },
    [startSend],
  );

  // ── Pipeline step 3: pick a suggestion → load channels ──────────
  const pickSuggestion = useCallback(
    (messageId: number, suggestionId: string) => {
      // Read the picked suggestion synchronously from the latest messages, THEN
      // mark it picked. (Reading a value assigned inside the setMessages updater
      // is unreliable and previously froze the flow after "✓ Выбрано".)
      const picked = messagesRef.current
        .find((m) => m.id === messageId)
        ?.picker?.suggestions.find((s) => s.id === suggestionId);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId && m.picker ? { ...m, picker: { ...m.picker, pickedId: suggestionId } } : m,
        ),
      );
      if (!picked) return;
      const chosen = picked;

      // Draft preview card.
      const draftId = nid.current++;
      append({
        id: draftId,
        role: 'assistant',
        kind: 'draft',
        text: 'Черновики под выбранного кандидата:',
        draft: draftCardFromSuggestion(chosen),
      });

      // Channel-selection card (starts loading). Links back to the draft card so
      // publish can pick up any operator edits (B5).
      const channelsId = nid.current++;
      append({
        id: channelsId,
        role: 'assistant',
        kind: 'channels',
        text: 'Куда публикуем? Выберите каналы и подтвердите.',
        channels: {
          suggestionId,
          options: [],
          selected: [],
          loading: true,
          confirmed: false,
          draftMessageId: draftId,
        },
      });

      void loadChannels(chosen).then(
        (options) => {
          // Pre-select all ready channels by default.
          const selected = options.filter((o) => o.ready).map((o) => o.id);
          patch(channelsId, (m) =>
            m.channels
              ? { ...m, channels: { ...m.channels, options, selected, loading: false } }
              : m,
          );
        },
        (err) => {
          patch(channelsId, (m) =>
            m.channels
              ? {
                  ...m,
                  channels: {
                    ...m.channels,
                    loading: false,
                    error: err instanceof Error ? err.message : 'Не удалось загрузить каналы.',
                  },
                }
              : m,
          );
        },
      );
    },
    [append, patch],
  );

  const toggleChannel = useCallback(
    (messageId: number, channelId: string) => {
      patch(messageId, (m) => {
        if (!m.channels || m.channels.confirmed) return m;
        const has = m.channels.selected.includes(channelId);
        const selected = has
          ? m.channels.selected.filter((id) => id !== channelId)
          : [...m.channels.selected, channelId];
        return { ...m, channels: { ...m.channels, selected } };
      });
    },
    [patch],
  );

  // ── Pipeline step 4: create → preflight → publish ───────────────
  const confirmChannels = useCallback(
    (messageId: number) => {
      // Resolve the channels card + its picked suggestion synchronously from the
      // latest messages, THEN mark confirmed (same reason as pickSuggestion).
      const current = messagesRef.current;
      const card: ChannelsCard | undefined = current.find((m) => m.id === messageId)?.channels;
      let picked: PromotionSuggestion | undefined;
      if (card) {
        for (const m of current) {
          const found = m.picker?.suggestions.find((s) => s.id === card.suggestionId);
          if (found) {
            picked = found;
            break;
          }
        }
      }
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId && m.channels ? { ...m, channels: { ...m.channels, confirmed: true } } : m,
        ),
      );

      if (!card || !picked) return;
      const chosen = picked;
      const selectedOptions = card.options.filter((o) => card!.selected.includes(o.id));
      if (selectedOptions.length === 0) {
        notify('Выберите хотя бы один канал');
        patch(messageId, (m) =>
          m.channels ? { ...m, channels: { ...m.channels, confirmed: false } } : m,
        );
        return;
      }

      const enabledPlatforms = Array.from(new Set(selectedOptions.map((o) => o.platform)));
      const selectedChannels: SelectedChannel[] = selectedOptions.map((o) => ({
        id: o.id,
        platform: o.platform,
        method: o.method,
        label: o.label,
        ...(o.bufferChannelId ? { buffer_channel_id: o.bufferChannelId } : {}),
      }));

      // B5: pick up operator edits from the sibling draft card (empty when the
      // operator never touched a draft — default path stays byte-identical).
      const edits =
        card.draftMessageId != null
          ? current.find((m) => m.id === card.draftMessageId)?.draft?.edits
          : undefined;

      const publishId = nid.current++;
      append({
        id: publishId,
        role: 'assistant',
        kind: 'publish',
        text: 'Создаю пост и запускаю публикацию…',
        publish: { results: [], status: 'creating' },
      });

      void publishFlow(chosen, enabledPlatforms, selectedChannels, publishId, edits).catch((err) => {
        patch(publishId, {
          id: publishId,
          role: 'assistant',
          kind: 'error',
          text: err instanceof Error ? err.message : 'Публикация не удалась.',
        });
      });
    },
    [append, notify, patch],
  );

  // Create → preflight → publish, updating the publish card at each step.
  const publishFlow = useCallback(
    async (
      suggestion: PromotionSuggestion,
      enabledPlatforms: Platform[],
      selectedChannels: SelectedChannel[],
      publishId: number,
      edits?: Partial<Record<Platform, string>>,
    ) => {
      // B5: apply operator edits over the suggestion's rendered drafts. Only the
      // `body` is overridden; every other draft field (title/tags/image/link) is
      // preserved. No edits → the exact same `suggestion.drafts` object shape.
      const drafts = applyDraftEdits(suggestion.drafts, edits);
      const created = await createFromChat({
        source: suggestion.source,
        drafts,
        enabled_platforms: enabledPlatforms,
        selected_channels: selectedChannels,
      });
      if (!created.post) throw new Error(created.error ?? 'Не удалось создать пост.');
      const postId = created.post.id;

      patch(publishId, (m) =>
        m.publish ? { ...m, publish: { ...m.publish, postId, status: 'preflight' } } : m,
      );

      const pf = await preflight(postId, enabledPlatforms);
      if (!pf.can_publish || pf.ready_platforms.length === 0) {
        patch(publishId, (m) =>
          m.publish
            ? {
                ...m,
                text: pf.message ?? 'Каналы не готовы к публикации.',
                publish: { ...m.publish, postId, status: 'blocked' },
              }
            : m,
        );
        notify('Публикация заблокирована — проверьте каналы');
        return;
      }

      patch(publishId, (m) =>
        m.publish ? { ...m, publish: { ...m.publish, postId, status: 'publishing' } } : m,
      );

      const outcome = await publish(postId, pf.ready_platforms);
      const ok = outcome.results.filter((r) => r.success).length;
      patch(publishId, (m) =>
        m.publish
          ? {
              ...m,
              text:
                ok > 0
                  ? `Опубликовано в ${ok} ${plural(ok, 'канал', 'канала', 'каналов')}.`
                  : outcome.message ?? 'Публикация завершена.',
              publish: {
                ...m.publish,
                postId,
                results: outcome.results,
                message: outcome.message,
                status: 'done',
              },
            }
          : m,
      );
      notify(ok > 0 ? 'Готово — пост опубликован' : 'Публикация завершена');
    },
    [notify, patch],
  );

  // ── Per-platform draft tools (B5 + B7) ──────────────────────────
  // These mutate ONLY the `draft` message's local card state. The unedited path
  // is unchanged: with no edits, confirmChannels falls back to the suggestion's
  // rendered drafts exactly as before.

  const setDraftEditing = useCallback(
    (messageId: number, platform: Platform | null) => {
      patch(messageId, (m) =>
        m.draft ? { ...m, draft: { ...m.draft, editing: platform } } : m,
      );
    },
    [patch],
  );

  const setDraftEdit = useCallback(
    (messageId: number, platform: Platform, body: string) => {
      patch(messageId, (m) =>
        m.draft ? { ...m, draft: { ...m.draft, edits: { ...m.draft.edits, [platform]: body } } } : m,
      );
    },
    [patch],
  );

  const appendHashtags = useCallback(
    (messageId: number, platform: Platform, body: string) => {
      void fetchHashtags({ body, platform }).then(
        (res) => {
          const tags = (res.hashtags ?? []).map((t) => t.replace(/^#/, '')).filter(Boolean);
          if (tags.length === 0) {
            notify('Хэштеги не предложены');
            return;
          }
          patch(messageId, (m) =>
            m.draft ? { ...m, draft: { ...m.draft, extraTags: { ...m.draft.extraTags, [platform]: tags } } } : m,
          );
          notify('Хэштеги добавлены');
        },
        (e) => notify(e instanceof Error ? e.message : 'Не удалось подобрать хэштеги'),
      );
    },
    [notify, patch],
  );

  const translateDraft = useCallback(
    (messageId: number, platform: Platform, body: string, language: 'ru' | 'en') => {
      const direction = language === 'ru' ? 'ru_to_en' : 'en_to_ru';
      const target: 'ru' | 'en' = language === 'ru' ? 'en' : 'ru';
      const source: SourceContent = { title: '', body, tags: [], language };
      void fetchTranslate({ source, direction, count: 1 }).then(
        (res) => {
          const variant = res.translations?.[0];
          const translatedBody = variant?.body?.trim();
          if (!translatedBody) {
            notify(res.error ?? 'Перевод недоступен');
            return;
          }
          patch(messageId, (m) =>
            m.draft
              ? {
                  ...m,
                  draft: {
                    ...m.draft,
                    translated: { ...m.draft.translated, [platform]: { language: variant?.language ?? target, body: translatedBody } },
                  },
                }
              : m,
          );
        },
        (e) => notify(e instanceof Error ? e.message : 'Не удалось перевести'),
      );
    },
    [notify, patch],
  );

  const clearTranslation = useCallback(
    (messageId: number, platform: Platform) => {
      patch(messageId, (m) => {
        if (!m.draft?.translated) return m;
        const { [platform]: _removed, ...rest } = m.draft.translated;
        return { ...m, draft: { ...m.draft, translated: rest } };
      });
    },
    [patch],
  );

  const regen = useCallback(
    (id: number, regenMode: BriefMode) => {
      patch(id, { id, role: 'assistant', kind: 'thinking', mode: regenMode });
      setTimeout(() => {
        patch(id, buildContent(regenMode, '', id));
        notify('Готовы новые варианты');
      }, 800);
    },
    [notify, patch],
  );

  const copyText = useCallback(
    (text: string) => {
      try {
        if (navigator.clipboard) void navigator.clipboard.writeText(text);
      } catch {
        /* clipboard unavailable — silently skip, toast still confirms intent */
      }
      notify('Текст скопирован');
    },
    [notify],
  );

  // ── Timezone (B7) — persist + optimistically reflect ─────────────
  const setTimezone = useCallback(
    async (tz: string) => {
      setTimezoneState(tz); // optimistic; the display uses it immediately
      try {
        const saved = await savePreferences(tz);
        if (saved.timezone) setTimezoneState(saved.timezone);
        notify('Часовой пояс сохранён');
      } catch (e) {
        notify(e instanceof Error ? e.message : 'Не удалось сохранить часовой пояс');
      }
    },
    [notify],
  );

  // ── Templates (B4) — seed the composer from a template's source ──
  const useTemplate = useCallback(
    (source: SourceContent) => {
      // Compose a natural-language brief from the template so the live pipeline
      // (chatPlan → suggestions) picks it up like any other prompt.
      const parts = [source.title, source.body].filter((s) => typeof s === 'string' && s.trim());
      const seeded = parts.join('\n\n').trim();
      setScreen('chat');
      setView('home');
      setActiveChat(null);
      setDraft(seeded);
      notify('Шаблон загружен в поле ввода');
    },
    [notify],
  );

  return {
    view,
    screen,
    mode,
    draft,
    messages,
    activeChat,
    toast,
    timezone,
    scrollRef,
    setScreen,
    setMode,
    setDraft,
    goHome,
    send,
    sendSuggestion,
    loadChat,
    regen,
    copyText,
    notify,
    useTemplate,
    setTimezone,
    pickSuggestion,
    toggleChannel,
    confirmChannels,
    setDraftEditing,
    setDraftEdit,
    appendHashtags,
    translateDraft,
    clearTranslation,
  };
}

// ── Channel discovery — direct (readiness) + Buffer ───────────────

/**
 * Build the channel options for a picked suggestion:
 *   • direct = telegram + twitter, ready flag from /connections/health readiness;
 *   • buffer = channels from the first Buffer organization, ready = !isQueuePaused.
 * All reads are soft (mock fallback) except Buffer (strict, may 429) — we swallow
 * Buffer failures so the direct channels still render.
 */
async function loadChannels(_suggestion: PromotionSuggestion): Promise<ChatChannelOption[]> {
  const options: ChatChannelOption[] = [];

  // Direct channels from connection + readiness (soft reads, never throw).
  const [connections, health] = await Promise.all([getConnections(), getHealth()]);
  const readinessByPlatform = new Map(health.data.map((r) => [r.platform, r]));
  const connByPlatform = new Map(connections.data.map((c) => [c.platform, c]));

  for (const platform of DIRECT_PLATFORMS) {
    const readiness = readinessByPlatform.get(platform);
    const conn = connByPlatform.get(platform);
    const ready = readiness?.ready ?? conn?.status === 'connected';
    const reasons = readiness?.reasons ?? [];
    const warnings = readiness?.warnings ?? [];
    options.push({
      id: `direct:${platform}`,
      platform,
      method: 'direct',
      label: labelForPlatform(platform),
      detail: ready ? 'Прямая публикация' : reasons[0] ?? 'Канал не подключён',
      ready,
      account: readiness?.account,
      reasons,
      warnings,
    } as ChatChannelOption);
  }

  // Buffer channels (strict; 429 or missing config → skip silently).
  try {
    const orgs = await bufferOrganizations();
    const org = orgs[0];
    if (org) {
      const channels = await bufferChannels(org.id);
      for (const ch of channels) {
        const ready = ch.isQueuePaused !== true;
        const name = ch.displayName ?? ch.name ?? ch.service;
        options.push({
          id: `buffer:${ch.id}`,
          platform: bufferServiceToPlatform(ch.service),
          method: 'buffer',
          label: `${name} · Buffer`,
          detail: ready ? `Очередь Buffer (${ch.service})` : 'Очередь Buffer на паузе',
          ready,
          avatar: ch.avatar,
          bufferChannelId: ch.id,
          reasons: ready ? [] : ['Очередь Buffer на паузе'],
          warnings: [],
        });
      }
    }
  } catch {
    /* Buffer unavailable (not configured / 429) — direct channels still render */
  }

  return options;
}

// ── Small mapping helpers ─────────────────────────────────────────

function labelForPlatform(p: Platform): string {
  const map: Partial<Record<Platform, string>> = {
    telegram: 'Telegram',
    twitter: 'X (Twitter)',
  };
  return map[p] ?? p;
}

/** Best-effort map of a Buffer service string onto our Platform union. */
function bufferServiceToPlatform(service: string): Platform {
  const s = service.toLowerCase();
  if (s.includes('telegram')) return 'telegram';
  if (s.includes('twitter') || s === 'x') return 'twitter';
  if (s.includes('instagram')) return 'instagram';
  if (s.includes('thread')) return 'threads';
  if (s.includes('facebook')) return 'facebook';
  if (s.includes('linkedin')) return 'linkedin';
  if (s.includes('youtube')) return 'youtube';
  if (s.includes('tiktok')) return 'tiktok';
  return 'twitter';
}

/** Normalize a chat-plan `platforms` field (array OR delimited string). */
function normalizePlatforms(value: string[] | string | undefined): Platform[] | null {
  if (!value) return null;
  const known: Platform[] = [
    'telegram',
    'twitter',
    'instagram',
    'threads',
    'facebook',
    'linkedin',
    'youtube',
    'tiktok',
    'reddit',
    'producthunt',
  ];
  const raw = Array.isArray(value) ? value : value.split(/[,;\s]+/);
  const out = raw
    .map((v) => v.trim().toLowerCase())
    .filter((v): v is Platform => (known as string[]).includes(v));
  return out.length > 0 ? out : null;
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

/**
 * Overlay operator draft edits (B5) onto a suggestion's rendered drafts. Only a
 * platform with a non-empty edit gets its copy (`text`) replaced; all other draft
 * fields are preserved. With no edits this returns the original `drafts` untouched
 * so the default publish path is byte-for-byte identical to before.
 *
 * NB: PlatformDraft stores the copy in `text` (not `body`) — overriding `body`
 * left the operator's edit invisible to the worker, which publishes `text`.
 */
function applyDraftEdits(
  drafts: Record<Platform, PlatformDraft>,
  edits?: Partial<Record<Platform, string>>,
): Record<Platform, PlatformDraft> {
  if (!edits) return drafts;
  const platforms = Object.keys(edits) as Platform[];
  const touched = platforms.filter((p) => {
    const edited = edits[p];
    return typeof edited === 'string' && drafts[p] != null && edited !== drafts[p].text;
  });
  if (touched.length === 0) return drafts;

  const next: Record<Platform, PlatformDraft> = { ...drafts };
  for (const p of touched) {
    next[p] = { ...drafts[p], text: edits[p] as string };
  }
  return next;
}

export { CHATS };
